// Shared lab / platform → HQ country datasets used by Models, Countries, Map and Platforms.
// Country = HQ of the releasing lab (e.g. Google DeepMind → United Kingdom).

export const labCountry: Record<string, string> = {
  OpenAI: 'USA',
  Anthropic: 'USA',
  'Google DeepMind': 'GBR',
  xAI: 'USA',
  Meta: 'USA',
  DeepSeek: 'CHN',
  'Alibaba Qwen': 'CHN',
  'Mistral AI': 'FRA',
  'Moonshot AI': 'CHN',
  'Zhipu AI (Z.ai)': 'CHN',
  MiniMax: 'CHN',
  'Nous Research': 'USA',
  Cohere: 'CAN',
  Microsoft: 'USA',
  Baidu: 'CHN',
  'ByteDance Seed': 'CHN',
  Tencent: 'CHN',
  StepFun: 'CHN',
  '01.AI': 'CHN',
  Baichuan: 'CHN',
  'Shanghai AI Laboratory': 'CHN',
  SenseTime: 'CHN',
  iFlytek: 'CHN',
  Xiaomi: 'CHN',
  'Ant Group': 'CHN',
  Meituan: 'CHN',
  Huawei: 'CHN',
  Amazon: 'USA',
  NVIDIA: 'USA',
  'Technology Innovation Institute': 'ARE',
  'AI21 Labs': 'ISR',
  'Sarvam AI': 'IND',
  'LG AI Research': 'KOR',
  'Swiss AI Initiative': 'CHE',
  'Allen Institute for AI': 'USA',
};

/** OpenRouter model-id prefix (before the "/") → HQ country of the publishing org. */
export const openRouterPrefixCountry: Record<string, string> = {
  openai: 'USA', anthropic: 'USA', google: 'GBR', 'meta-llama': 'USA', 'x-ai': 'USA', mistralai: 'FRA', deepseek: 'CHN', qwen: 'CHN',
  moonshotai: 'CHN', 'z-ai': 'CHN', thudm: 'CHN', minimax: 'CHN', nousresearch: 'USA', cohere: 'CAN', microsoft: 'USA', amazon: 'USA',
  nvidia: 'USA', perplexity: 'USA', baidu: 'CHN', tencent: 'CHN', bytedance: 'CHN', 'bytedance-seed': 'CHN', inception: 'USA', ai21: 'ISR',
  liquid: 'USA', 'stepfun-ai': 'CHN', 'arcee-ai': 'USA', inflection: 'USA', tngtech: 'DEU', sarvamai: 'IND', 'ibm-granite': 'USA',
  allenai: 'USA', '01-ai': 'CHN', upstage: 'KOR', 'agentica-org': 'USA', alibaba: 'CHN', 'meituan': 'CHN', 'kwaipilot': 'CHN',
  'swiss-ai': 'CHE', 'inclusionai': 'CHN', 'xiaomi': 'CHN', 'baichuan': 'CHN', 'tiiuae': 'ARE', 'opengvlab': 'CHN', 'internlm': 'CHN',
};

/** Curated lab name → OpenRouter publisher prefix. */
export const labPrefix: Record<string, string> = {
  OpenAI: 'openai', Anthropic: 'anthropic', 'Google DeepMind': 'google', xAI: 'x-ai', Meta: 'meta-llama', DeepSeek: 'deepseek',
  'Alibaba Qwen': 'qwen', 'Mistral AI': 'mistralai', 'Moonshot AI': 'moonshotai', 'Zhipu AI (Z.ai)': 'z-ai', MiniMax: 'minimax',
  'Nous Research': 'nousresearch', Cohere: 'cohere', Microsoft: 'microsoft', Baidu: 'baidu', Tencent: 'tencent', 'ByteDance Seed': 'bytedance-seed',
  StepFun: 'stepfun-ai', Amazon: 'amazon', NVIDIA: 'nvidia', 'AI21 Labs': 'ai21', 'Allen Institute for AI': 'allenai', 'Sarvam AI': 'sarvamai',
  'Ant Group': 'inclusionai', Meituan: 'meituan', Xiaomi: 'xiaomi', 'Swiss AI Initiative': 'swiss-ai', 'Technology Innovation Institute': 'tiiuae',
};

export const countryNames: Record<string, string> = {
  USA: 'United States', CHN: 'China', GBR: 'United Kingdom', FRA: 'France', CAN: 'Canada', ISR: 'Israel', DEU: 'Germany',
  IND: 'India', KOR: 'South Korea', JPN: 'Japan', ARE: 'United Arab Emirates', NLD: 'Netherlands', SGP: 'Singapore', CHE: 'Switzerland',
};

export const cca2Of: Record<string, string> = {
  USA: 'us', CHN: 'cn', GBR: 'gb', FRA: 'fr', CAN: 'ca', ISR: 'il', DEU: 'de', IND: 'in', KOR: 'kr', JPN: 'jp', ARE: 'ae', NLD: 'nl', SGP: 'sg', CHE: 'ch',
};

/** Platforms in data/stack.ts → HQ country. */
export const platformCountry: Record<string, string> = {
  OpenRouter: 'USA', 'LiteLLM proxy': 'USA', Portkey: 'USA', 'Hugging Face Inference Providers': 'FRA', 'Together AI': 'USA', 'Fireworks AI': 'USA',
  Groq: 'USA', 'Cerebras Inference': 'USA', Replicate: 'USA', 'OpenAI API': 'USA', 'Anthropic API': 'USA', 'Google AI Studio / Vertex AI': 'USA',
  'xAI API': 'USA', 'Mistral La Plateforme': 'FRA', 'DeepSeek API': 'CHN',
  CoreWeave: 'USA', 'NVIDIA DGX Cloud': 'USA', Lambda: 'USA', Nebius: 'NLD', Crusoe: 'USA', FluidStack: 'GBR', 'Voltage Park': 'USA',
  RunPod: 'USA', 'Vast.ai': 'USA', AWS: 'USA', 'Google Cloud': 'USA', 'Microsoft Azure': 'USA', 'Oracle OCI': 'USA',
};

export interface RegionalPlatform {
  name: string;
  cca3: string;
  kind: 'Model API' | 'Inference cloud' | 'Compute';
  url: string;
}

/** National platforms not covered in the global stack tables. */
export const regionalPlatforms: RegionalPlatform[] = [
  { name: 'Krutrim Cloud', cca3: 'IND', kind: 'Inference cloud', url: 'https://cloud.olakrutrim.com' },
  { name: 'Sarvam API', cca3: 'IND', kind: 'Model API', url: 'https://www.sarvam.ai/apis' },
  { name: 'Yotta Shakti Cloud', cca3: 'IND', kind: 'Compute', url: 'https://shakticloud.ai' },
  { name: 'E2E Networks', cca3: 'IND', kind: 'Compute', url: 'https://www.e2enetworks.com' },
  { name: 'Scaleway', cca3: 'FRA', kind: 'Compute', url: 'https://www.scaleway.com/en/ai-solutions/' },
  { name: 'IONOS AI Model Hub', cca3: 'DEU', kind: 'Model API', url: 'https://cloud.ionos.com/managed/ai-model-hub' },
  { name: 'Aleph Alpha PhariaAI', cca3: 'DEU', kind: 'Model API', url: 'https://aleph-alpha.com' },
  { name: 'Alibaba Cloud Model Studio', cca3: 'CHN', kind: 'Model API', url: 'https://www.alibabacloud.com/en/product/modelstudio' },
  { name: 'Volcano Engine Ark', cca3: 'CHN', kind: 'Model API', url: 'https://www.volcengine.com/product/ark' },
  { name: 'Baidu Qianfan', cca3: 'CHN', kind: 'Model API', url: 'https://cloud.baidu.com/product/wenxinworkshop' },
  { name: 'SiliconFlow', cca3: 'CHN', kind: 'Inference cloud', url: 'https://siliconflow.cn' },
  { name: 'Sakura Internet', cca3: 'JPN', kind: 'Compute', url: 'https://www.sakura.ad.jp' },
  { name: 'Naver Cloud', cca3: 'KOR', kind: 'Model API', url: 'https://www.ncloud.com' },
  { name: 'Core42', cca3: 'ARE', kind: 'Compute', url: 'https://www.core42.ai' },
  { name: 'Nscale', cca3: 'GBR', kind: 'Compute', url: 'https://www.nscale.com' },
  { name: 'Cohere API', cca3: 'CAN', kind: 'Model API', url: 'https://cohere.com' },
  { name: 'AI21 Studio', cca3: 'ISR', kind: 'Model API', url: 'https://www.ai21.com/studio' },
];
